import React, { useState, useMemo } from 'react';
import { clsx, colorSet, useSnackbar } from '@components/Provider';
import useStyles from './useStyles';
import { Chip, Grid, IconButton } from '@material-ui/core';
import {
  CustomizedButton,
  CustomizedTooltip,
  CustomizedTypography
} from '@components/Customized';
import { Controller, useFormContext } from 'react-hook-form';
import OutlinedTextField from '@routes/Common/OutlinedTextField';
import useFiles from '@hooks/useFiles';
import { IRequestState, UploadedReferenceFileType } from '@modules/request';
import { formatFileSize } from '@utils/formatter';
import { Close } from '@material-ui/icons';
import { Close as CloseIcon, Information } from '@carbon/icons-react';
import { debounce } from 'lodash';
import LoadingPresenter from '@components/Loading';
import { CapaActivityType, CapaQuotationServiceType } from '@generated/graphql';
import useUserActionLog, {
  Enum_Action_Button_Enum
} from '@hooks/useUserActionLog';
import { DialogTypes } from './types';
import howToContents from './Dialogs/HowtoModal/HowToContents';

interface Props {
  serviceType: CapaQuotationServiceType;
  referenceFiles?: UploadedReferenceFileType[];
  handleUpdateRfqInfo: (payload: Partial<IRequestState>) => void;
  onOpenDialog: (type: keyof DialogTypes) => void;
}

const RequestForQuotationRef = ({
  serviceType,
  referenceFiles = [],
  handleUpdateRfqInfo,
  onOpenDialog
}: Props) => {
  const classes = useStyles();
  const { onInsertUserActionLogOne } = useUserActionLog();
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const { onUploadFiles } = useFiles();
  const { control, getValues } = useFormContext();

  const [guideToolTipOpen, setGuideToolTipOpen] = useState(true);

  const hasHowToModal =
    serviceType && Object.keys(howToContents).includes(serviceType);

  const onOpen = () => {
    const input = document.getElementById('ref-upload-input');

    if (input) {
      input.click();
    }
  };

  const onChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length !== 0) {
      let files: File[] = [];
      const filesList = e.target.files;

      for (let index = 0; index < filesList.length; index++) {
        files.push(filesList.item(index) as File);
      }

      setLoading(true);

      const res = await onUploadFiles(files);

      if (res.error) {
        setLoading(false);

        return enqueueSnackbar('파일 업로드를 실패하였습니다.', {
          variant: 'error'
        });
      }

      if (!res.error) {
        setLoading(false);

        handleUpdateRfqInfo({
          uploadedReferenceFiles: [...referenceFiles, ...res.files].map(
            (file) => ({
              fileId: file?.fileId ?? '',
              fileName: file?.fileName ?? '',
              fileSize: Number(file.fileSize)
            })
          )
        });

        return enqueueSnackbar('파일 업로드를 성공하였습니다.', {
          variant: 'success'
        });
      }
    }
  };

  const onDelete = (e: React.SyntheticEvent<HTMLButtonElement>) => {
    if (e.currentTarget) {
      handleUpdateRfqInfo({
        uploadedReferenceFiles: referenceFiles.filter(
          (file) => file?.fileId !== e.currentTarget.id
        )
      });
    }
  };

  const onKeydown = debounce(() => {
    const detailedRequest = getValues('detailedRequest');
    handleUpdateRfqInfo({
      detailedRequest: detailedRequest
    });
  }, 350);

  const logId = useMemo(() => {
    if (serviceType === CapaQuotationServiceType.Cnc) {
      return Enum_Action_Button_Enum.Button_RFQ_CNC_Etc;
    }

    if (serviceType === CapaQuotationServiceType.Printing) {
      return Enum_Action_Button_Enum.Button_RFQ_PRINTING_Etc;
    }

    if (serviceType === CapaQuotationServiceType.Inj) {
      return Enum_Action_Button_Enum.Button_RFQ_INJ_Etc;
    }

    if (serviceType === CapaQuotationServiceType.SheetMetal) {
      return Enum_Action_Button_Enum.Button_RFQ_SHEET_METAL_Etc;
    }

    if (serviceType === CapaQuotationServiceType.Casting) {
      return Enum_Action_Button_Enum.Button_RFQ_CASTING_Etc;
    }
  }, [serviceType]);

  const onLog = () => {
    if (logId) {
      return onInsertUserActionLogOne(logId, CapaActivityType.ButtonClick);
    }
  };

  return (
    <section className={clsx(classes['rfq-ref'], classes['rfq-info-section'])}>
      {loading && <LoadingPresenter open />}
      <Controller
        name="referenceFiles"
        defaultValue={[]}
        control={control}
        render={(render) => (
          <input
            multiple
            onChange={onChange}
            type="file"
            id="ref-upload-input"
          />
        )}
      />

      <CustomizedTypography
        className="title"
        align="center"
        color="textPrimary"
        gutterBottoms={8}
      >
        도면 상세 정보를 입력해주세요
      </CustomizedTypography>

      {hasHowToModal && (
        <CustomizedTooltip
          open={guideToolTipOpen}
          backgroundcolor={colorSet.primary500}
          arrow
          title={
            <Grid container alignItems="center">
              <CustomizedTypography variant="caption" color="initial">
                가이드라인을 읽고 작성해주시면
                <br />
                더욱 정확한 견적을 받을 수 있습니다.
              </CustomizedTypography>
              <IconButton onClick={() => setGuideToolTipOpen(false)}>
                <CloseIcon size={20} color={colorSet.gray000} />
              </IconButton>
            </Grid>
          }
        >
          <Chip
            className="rfq-header-guide-chip"
            icon={<Information size={14} color={colorSet.gray700} />}
            label={
              <CustomizedTypography variant="body2" color="textPrimary">
                정확한 견적을 받는 법
              </CustomizedTypography>
            }
            clickable
            onClick={() => onOpenDialog('howTo')}
          />
        </CustomizedTooltip>
      )}

      <div className="ref-field detailedRequest">
        <CustomizedTypography
          variant="body1"
          color="textPrimary"
          fontWeight="500"
          gutterBottoms={4}
        >
          상세 요청사항 (선택)
        </CustomizedTypography>
        <CustomizedTypography
          variant="body2"
          color="textSecondary"
          gutterBottoms={16}
        >
          도면의 치수, 가공 용도, 상세 가공 요청을 파트너에게 알려주세요. 요청
          사항이 상세할수록, 업체로부터 정확한 답변을 받을 확률이 높아집니다.
        </CustomizedTypography>

        <Controller
          control={control}
          name="detailedRequest"
          defaultValue=""
          render={(render) => (
            <OutlinedTextField
              {...render}
              onChange={(e) => render.onChange(e.target.value.trimStart())}
              fullWidth
              onClick={onLog}
              multiline
              minRows={6}
              maxRows={6}
              placeholder="공차, 조립성, 도색, 표면 처리등 상세한 가공 요청을 입력해 주세요."
              onKeyDown={() => onKeydown()}
            />
          )}
        />
      </div>
      <div className="ref-field ref-files">
        <CustomizedButton
          variant="outlined"
          color="primary"
          size="small"
          className="ref-upload-button"
          onClick={onOpen}
        >
          파일 첨부
        </CustomizedButton>
        <CustomizedTypography
          variant="body1"
          color="textPrimary"
          fontWeight="500"
          gutterBottoms={4}
        >
          참고 파일 (선택)
          <span className="total">총 {referenceFiles.length}개</span>
        </CustomizedTypography>
        <CustomizedTypography
          variant="body2"
          color="textSecondary"
          gutterBottoms={12}
        >
          도면, 프로젝트 이해에 도움이 되는 기타 파일을 첨부해주세요.
        </CustomizedTypography>

        <div className="ref-files-list">
          {referenceFiles.map((file, index) => (
            <div className="ref-file" key={index}>
              <CustomizedTypography
                variant="caption"
                color="textPrimary"
                fontWeight="bold"
              >
                {(() => {
                  if (file.fileName) {
                    const fileName =
                      file.fileName && file.fileName.length > 20
                        ? file.fileName.slice(0, 20) + '...'
                        : file.fileName.slice(
                            0,
                            file.fileName.lastIndexOf('.')
                          );

                    const extension =
                      file.fileName &&
                      file.fileName
                        .slice(file.fileName.lastIndexOf('.') + 1)
                        .toLowerCase();

                    return fileName + '.' + extension;
                  }

                  return '';
                })()}
              </CustomizedTypography>

              <CustomizedTypography variant="caption" color="textSecondary">
                {formatFileSize(file?.fileSize ?? 0)}
              </CustomizedTypography>

              <IconButton size="small" id={file?.fileId} onClick={onDelete}>
                <Close color="secondary" />
              </IconButton>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default RequestForQuotationRef;
